<script lang="ts">
import dayjs from "dayjs";
import {asValue} from "$lib/utils/lenkradDate";
import EmployeeInfo from "$items/employee/EmployeeInfo.svelte";
import CardLayout from "$items/CardLayout.svelte";
import Photos from "$items/employee/Photos.svelte";
import GeneralInfo from "$items/employee/GeneralInfo.svelte";
import {
    ClipboardSolid,
    EditSolid,
    ExclamationCircleSolid, FolderSolid, GridOutline,
    IconSolid, InfoCircleOutline,
    InfoCircleSolid, ListOutline, MessagesSolid,
    PenOutline,
    TicketOutline, UserSolid
} from "flowbite-svelte-icons";
import TaxAndInsurance from "$items/employee/TaxAndInsurance.svelte";
import {Button, Textarea, Timeline, TimelineItem, Tooltip} from "flowbite-svelte";
import Reminder from "$items/employee/Reminder.svelte";
import Qualifications from "$items/employee/Qualifications.svelte";
import BankAccount from "$items/employee/BankAccount.svelte";
import {onMount, setContext} from "svelte";
import {get} from "$lib/api";
import {type Writable, writable} from "svelte/store";
import type {Employee} from "$lib/types/employee";
import EmployeeAvatar from "$lib/components/EmployeeAvatar.svelte";
import {page} from "$app/stores";
import {listView} from "$lib/stores/viewStore";
import EmploymentStatus from "$lib/components/EmploymentStatus.svelte";
import TabButton from "$items/TabButton.svelte";

let employee: Writable<Employee> = writable();
setContext('employee', employee)
let trigger_submits = writable(new Set<HTMLButtonElement | HTMLFormElement>())
setContext('trigger_submits', trigger_submits)

const ICONS = [
    {type: 'info', icon: InfoCircleSolid, color: 'bg-primary-200 text-primary-600'},
    {type: 'important', icon: ExclamationCircleSolid, color: 'bg-red-200 text-red-600'},
]
onMount(async () =>{
    const firstEmployee = (await get('/employees?page=1&pageSize=1&search=test'))?.collection[0]
    $employee = await get('/employee/'+firstEmployee.uuid);

    console.log($employee)
})
</script>
{#if $employee?.id}
<div class="flex flex-col md:flex-row items-start gap-3 px-4 pt-7">
    <div class="flex flex-1 flex-row items-center gap-3">
        <EmployeeAvatar  employee={$employee} size="2xl"/>
        <div class="flex flex-col items-start gap-1">
            <h1 class="text-base text-neutral-600 sm:text-lg md:text-2xl font-bold">{$employee.name}</h1>

            <span class="text-neutral-600">Unit: Demo Unit</span>
            <div class="text-neutral-600 text-[14px] flex gap-2"><TicketOutline class="text-primary-400 "/> <span>{$employee.employeeNumber || 'Bewerber'}</span></div>
        </div>

    </div>
    <div class="flex gap-3 mb-2">
        <Button size="xs" icon={EditSolid}>
            <PenOutline class="w-4 mr-2"/>
            <span>Button 1</span>
        </Button>
        <Button size="xs" icon={EditSolid}>
            <PenOutline class="w-4 mr-2"/>
            <span>Button 2</span>
        </Button>
    </div>
</div>
    <div class="flex flex-row items-end justify-between w-full md:border-b border-gray-300 mt-5">
        <div class="flex flex-wrap md:flex-nowrap flex-row justify-start items-center gap-3 md:gap-0">
            <TabButton href="/employee/{$page.params.uuid}" icon={UserSolid}
                       title="Stammdaten"/>
            <TabButton href="/employee/{$page.params.uuid}/vertrag" icon={ClipboardSolid}
                       title="Vertrag / Gehalt"/>
            <TabButton href="/employee/{$page.params.uuid}/dokumente" icon={FolderSolid}
                       title="Dokumente"/>
            {#if $employee.userId}
                <TabButton href="/employee/{$page.params.uuid}/connect" icon={MessagesSolid}
                           title="Connect"/>
            {/if}
        </div>

        <div class="max-w-sm w-full pl-[1.1rem] flex justify-between">
            <div class="h-[50px] flex gap-1 items-center">
                <div class="text-neutral-600 dark:text-gray-400 whitespace-nowrap relative"><InfoCircleOutline class="text-primary-400"/></div>
                <div ><EmploymentStatus  size="sm" bind:status={$employee.employmentStatus} uuid={$employee.uuid}/></div>
            </div>
            <div class="pr-3 pt-1">
                <Button on:click={() => $listView = !$listView} color="alternative" pill class="!p-2" >
                    {#if $listView}
                        <ListOutline class="text-primary-600" />
                    {:else}
                        <GridOutline class="text-primary-600" />
                    {/if}
                </Button>
                <Tooltip>Ansicht wechseln</Tooltip>
            </div>

        </div>
    </div>
    <div class="pt-12 px-0">
        <div class="flex flex-col lg:flex-row gap-3">
            <div class="flex flex-col gap-7 xl:max-w-sm">
                <GeneralInfo employee={$employee}/>
                <CardLayout title={$employee.employmentStatus !== 'active' ? 'Ich will ins Team weil...' : 'Notiz'} className="sticky top-24">
                    <Textarea disabled rows="4" value={$employee?.cv?.note}/>
                </CardLayout>
                <Photos employee={$employee}/>

            </div>

            <div class="flex flex-col 2xl:flex-row gap-3 w-full">
                <div class="flex flex-col w-full gap-7">
                    <Reminder />
                    <EmployeeInfo/>
                    <Qualifications/>
                    <TaxAndInsurance/>
                    <BankAccount/>
                </div>

                <div class="max-w-sm w-full">
                    <CardLayout display={false} className="mb-4" title="Add to Timeline" accordion>
                        <form class="mb-7 flex flex-col gap-2" method="post" action="?/publish_to_timeline" use:enhance>
                            <Textarea id="textarea-id" placeholder="Your message" rows="4" name="content"/>
                            <Button type="submit" class="ml-auto" variant="primary">Add work log</Button>
                        </form>
                    </CardLayout>
                    <div class="px-7 mt-8">
                        <Timeline order="vertical" class="w-full">
                            {#each $employee.timeline.sort((a, b) => b.createdAt.stamp - a.createdAt.stamp) as timelineItem}
                                {@const icon = ICONS.find(x => x.type === timelineItem.type)}
                                <TimelineItem
                                        title={timelineItem.type}
                                        date={dayjs(asValue(timelineItem.createdAt)).format('DD.MM.YYYY HH:mm')}>
                                    <svelte:fragment slot="icon">
                                <span class="flex absolute -left-3 justify-center items-center w-6 h-6 {icon?.color} rounded-full ring-8 ring-white">
                                    <IconSolid icon="{icon?.icon}"
                                               class="w-3 h-3 "/>
                                </span>
                                    </svelte:fragment>
                                    <p class="text-gray-400 my- text-xs">
                                        {#if timelineItem.enteringEmployeeId}
                                            {timelineItem?.enteringAssociations[0]?.firstName + ' ' + timelineItem?.enteringAssociations[0]?.lastName}
                                        {:else}
                                            System
                                        {/if}
                                    </p>
                                    <div class="mb-4 text-base font-normal text-gray-500 overflow-x-auto">
                                        {@html timelineItem.content}
                                    </div>
                                </TimelineItem>

                            {/each}
                        </Timeline>
                    </div>

                </div>
            </div>

        </div>
    </div>

{/if}
